import type { GraphEdge, GraphNode, ProjectGraph } from "../types/ontology";
import { env } from "./env";

export interface WhyStep {
  node: GraphNode;
  edge?: GraphEdge;
  reason: string;
}

export interface WhyPath {
  score: number;
  steps: WhyStep[];
}

const CAUSAL_EDGES = new Set<GraphEdge["kind"]>([
  "RULE_FIRED_FROM",
  "RULE_RECOMMENDS",
  "RULE_BLOCKED_BY",
  "BLOCKED_BY",
  "DEPENDS_ON",
  "APPROVED_BY",
  "OF_SKU",
  "FOR_ROOM",
  "FOR_SKU",
  "OF",
  "PROMOTED_FROM",
  "APPLIES_TO",
]);

function reasonFor(edge: GraphEdge): string {
  switch (edge.kind) {
    case "RULE_FIRED_FROM":   return "a rule fired here";
    case "RULE_RECOMMENDS":   return "the rule recommended this";
    case "RULE_BLOCKED_BY":   return "blocked by a rule";
    case "BLOCKED_BY":        return "blocked by";
    case "DEPENDS_ON":        return "depends on";
    case "APPROVED_BY":       return "approved by";
    case "OF_SKU":            return "is the SKU for";
    case "FOR_ROOM":          return "budgeted for the room";
    case "FOR_SKU":           return "budgeted for the SKU";
    case "OF":                return "applies to";
    case "PROMOTED_FROM":     return "promoted from a project rule";
    case "APPLIES_TO":        return "matches a memory card";
    default:                  return edge.kind.toLowerCase();
  }
}

// BFS from a focus node through every causal edge — in BOTH directions.
// Provenance edges in this ontology can point either way depending on
// the kind (e.g. an Alert -[RULE_FIRED_FROM]-> Rule), so a "walk
// backward through incoming edges" hides half the chain. We now walk
// the full causal neighbourhood and label each step by edge direction.
//
// Depth-limited (env.VITE_WHY_MAX_DEPTH) and top-K-bounded so even on
// a 50k-node graph the answer stays inside the latency budget quoted
// in Spec 03 (W-04: P95 < 5s).
export function explain(graph: ProjectGraph, nodeId: string): WhyPath[] {
  const startNode = graph.nodes.find((n) => n.id === nodeId);
  if (!startNode) return [];

  const outgoingBySource = new Map<string, GraphEdge[]>();
  const incomingByTarget = new Map<string, GraphEdge[]>();
  for (const edge of graph.edges) {
    if (!CAUSAL_EDGES.has(edge.kind)) continue;
    const outList = outgoingBySource.get(edge.source) ?? [];
    outList.push(edge);
    outgoingBySource.set(edge.source, outList);
    const inList = incomingByTarget.get(edge.target) ?? [];
    inList.push(edge);
    incomingByTarget.set(edge.target, inList);
  }
  const nodeById = new Map<string, GraphNode>(graph.nodes.map((n) => [n.id, n]));

  const paths: WhyPath[] = [];

  type Frame = { path: WhyStep[]; visited: Set<string>; depth: number; score: number };
  const initial: Frame = {
    path: [{ node: startNode, reason: "starting point" }],
    visited: new Set([startNode.id]),
    depth: 0,
    score: 0,
  };
  const queue: Frame[] = [initial];

  while (queue.length > 0) {
    const frame = queue.shift();
    if (!frame) break;
    const lastStep = frame.path[frame.path.length - 1];
    if (!lastStep) continue;
    const last = lastStep.node.id;
    const outgoing = outgoingBySource.get(last) ?? [];
    const incoming = incomingByTarget.get(last) ?? [];
    const neighbours: { edge: GraphEdge; otherId: string; outbound: boolean }[] = [
      ...outgoing.map((e) => ({ edge: e, otherId: e.target, outbound: true })),
      ...incoming.map((e) => ({ edge: e, otherId: e.source, outbound: false })),
    ];
    if (neighbours.length === 0 || frame.depth >= env.VITE_WHY_MAX_DEPTH) {
      if (frame.path.length > 1) paths.push({ score: frame.score, steps: frame.path });
      continue;
    }
    for (const { edge, otherId } of neighbours) {
      if (frame.visited.has(otherId)) continue;
      const otherNode = nodeById.get(otherId);
      if (!otherNode) continue;
      const nextStep: WhyStep = { node: otherNode, edge, reason: reasonFor(edge) };
      queue.push({
        path: [...frame.path, nextStep],
        visited: new Set([...frame.visited, otherId]),
        depth: frame.depth + 1,
        score: frame.score + edge.weight,
      });
    }
  }

  paths.sort((a, b) => b.score - a.score);
  return paths.slice(0, env.VITE_WHY_TOP_K_PATHS);
}

export function narrate(path: WhyPath): string {
  if (path.steps.length === 0) return "";
  const parts: string[] = [];
  for (let i = 0; i < path.steps.length; i += 1) {
    const step = path.steps[i];
    if (!step) continue;
    if (i === 0) {
      parts.push(`**${step.node.label}** is in its current state because…`);
      continue;
    }
    const prev = path.steps[i - 1];
    if (!prev) continue;
    parts.push(`…${step.reason} **${step.node.label}** (${step.node.kind.toLowerCase()})`);
  }
  return parts.join("\n\n");
}
